import { useState, useEffect } from 'react';
import Joyride, { Step, CallBackProps, STATUS } from 'react-joyride';
import { useLocation } from 'react-router-dom';

export type TourType = 'onboarding' | 'dashboard' | 'captable' | 'compliance' | 'dataroom';

interface PlatformTourProps {
  tourType: TourType;
  run: boolean;
  onComplete: () => void;
}

const tourSteps: Record<TourType, Step[]> = {
  onboarding: [
    {
      target: 'body',
      content:
        "Welcome to CapLedger! Let's walk through setting up your first company. This will only take a few minutes.",
      placement: 'center',
    },
  ],
  dashboard: [
    {
      target: 'body',
      content:
        'Welcome to your dashboard! This is your command center for managing your cap table. Let me show you around.',
      placement: 'center',
    },
    {
      target: '[data-tour="company-selector"]',
      content:
        'Switch between companies here if you manage multiple entities. The selected company determines what data you see across the platform.',
    },
    {
      target: '[data-tour="stats-cards"]',
      content:
        'These cards show your key metrics at a glance: total shares issued, number of shareholders, transaction count, and company valuation.',
    },
    {
      target: '[data-tour="cap-table-chart"]',
      content:
        "This chart visualizes your ownership structure. Hover over segments to see detailed breakdowns of each shareholder's holdings.",
    },
    {
      target: '[data-tour="quick-actions"]',
      content:
        'Use these shortcuts to perform common tasks like adding shareholders, managing team access, or viewing detailed reports.',
    },
    {
      target: '[data-tour="help-menu"]',
      content:
        'Need help? Click here to ask Ella (our AI assistant), start guided tours, or view the glossary of terms.',
    },
  ],
  captable: [
    {
      target: 'body',
      content: 'This is your cap table - the definitive record of who owns what in your company.',
      placement: 'center',
    },
    {
      target: '[data-tour="shareholders-list"]',
      content:
        'All your shareholders are listed here with their shareholdings, ownership percentages, and contact details.',
    },
    {
      target: '[data-tour="add-shareholder"]',
      content:
        'Add new shareholders or investors here. You can also issue shares to them directly.',
    },
    {
      target: '[data-tour="export-actions"]',
      content:
        'Export your cap table, share register, or generate official reports for filing or investor updates.',
    },
  ],
  compliance: [
    {
      target: 'body',
      content:
        'Stay compliant with regulatory requirements. Ella monitors your compliance health and alerts you to issues.',
      placement: 'center',
    },
    {
      target: '[data-tour="risk-score"]',
      content: 'Your overall compliance health score. Click to see what factors are affecting it.',
    },
    {
      target: '[data-tour="upcoming-deadlines"]',
      content:
        'Never miss a filing deadline. We track all statutory deadlines based on your jurisdiction and company events.',
    },
    {
      target: '[data-tour="ella-advisor"]',
      content:
        'Ask Ella compliance questions. She has deep knowledge of South African company law and can provide instant guidance.',
    },
  ],
  dataroom: [
    {
      target: 'body',
      content: 'Your secure data room for managing sensitive company documents.',
      placement: 'center',
    },
    {
      target: '[data-tour="upload-zone"]',
      content:
        'Drag and drop files here or click to upload. All files are encrypted and access-controlled.',
    },
    {
      target: '[data-tour="folder-structure"]',
      content:
        'Organize documents into folders. Create separate folders for different fundraising rounds or due diligence processes.',
    },
    {
      target: '[data-tour="sharing-controls"]',
      content:
        'Share specific folders or files with investors via secure, time-limited links. Track who accessed what and when.',
    },
  ],
};

export function PlatformTour({ tourType, run, onComplete }: PlatformTourProps) {
  const [steps] = useState<Step[]>(tourSteps[tourType] || []);
  const location = useLocation();

  const handleJoyrideCallback = (data: CallBackProps) => {
    const { status } = data;
    const finishedStatuses: string[] = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(status as string)) {
      onComplete();
    }
  };

  useEffect(() => {
    // Store tour completion in localStorage
    const completedTours = JSON.parse(localStorage.getItem('completedTours') || '[]');
    if (run && !completedTours.includes(tourType)) {
      localStorage.setItem('completedTours', JSON.stringify([...completedTours, tourType]));
    }
  }, [run, tourType]);

  return (
    <Joyride
      steps={steps}
      run={run}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      styles={{
        options: {
          primaryColor: 'hsl(var(--primary))',
          textColor: 'hsl(var(--foreground))',
          backgroundColor: 'hsl(var(--background))',
          arrowColor: 'hsl(var(--background))',
          overlayColor: 'rgba(0, 0, 0, 0.5)',
        },
        tooltip: {
          borderRadius: '8px',
        },
        buttonNext: {
          backgroundColor: 'hsl(var(--primary))',
          color: 'hsl(var(--primary-foreground))',
        },
        buttonBack: {
          color: 'hsl(var(--muted-foreground))',
        },
      }}
    />
  );
}
